<template>
  <div class="fill-height">
    <v-card flat>
      <v-card-title class="py-0 px-1">
        <v-toolbar
          :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
          class="filter-toolbar"
          flat
        >
          <v-spacer />

          <filter-options
            v-model="filtersModel"
            :filters="filterData"
          />
        </v-toolbar>
      </v-card-title>

      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col cols="12">
            <v-data-table
              :footer-props="dataTable.footerProps"
              :headers="dataTableHeaders"
              :height="dataTable.resize.bodyHeight"
              :items="dataTable.data"
              :loading="dataTable.loading"
              :loading-text="dataTable.text.loadingText"
              :no-data-text="dataTable.text.noDataText"
              :no-results-text="dataTable.text.noResultsText"
              :options.sync="dataTable.options"
              :search="dataTable.search.terms"
              :server-items-length="dataTable.total"
              fixed-header
              item-key="Id"
            >
              <template #[`item.id`]="{item}">
                {{ item.Id }}
              </template>

              <template #[`item.type`]="{item}">
                <div
                  :class="$t(`Reports.ReportsNotify.Type.${item.Type}`).TitleColor"
                  class="font-weight-bold"
                >
                  {{ $t(`Reports.ReportsNotify.Type.${item.Type}`).Title }}
                </div>
              </template>

              <template #[`item.range`]="{item}">
                <div v-if="item.Range">
                  {{ item.Range }}
                </div>

                <div v-if="item.Properties?.Filter?.Period">
                  {{ getPeriodName(item.Properties?.Filter?.Period) }}
                </div>
              </template>

              <template #[`item.properties`]="{item}">
                <div v-if="item?.Properties?.hasOwnProperty('WithoutOpenHours')">
                  {{ item?.Properties?.WithoutOpenHours ? 'Ημερολογιακά' : 'Βάση Ωραρίου' }}
                </div>

                <div v-if="item?.Properties?.StaffId">
                  <strong>{{ $t('Common.SelectDateRangeDialog.Field.Staff.Label') }}:</strong> {{ getStaffName(item.Properties?.StaffId) }}
                </div>

                <div v-if="item?.Properties?.MachineId">
                  <strong>{{ $t('Common.SelectDateRangeDialog.Field.Guid.Label') }}:</strong> {{ getMachineName(item.Properties?.MachineId) }}
                </div>

                <!--
                <div>Filter.InvoiceType: {{ item.Properties?.Filter?.InvoiceType }}</div>
                <div>Filter.OrderBy: {{ item.Properties?.Filter?.OrderBy }}</div>
                <div>Filter.SortBy: {{ item.Properties?.Filter?.SortBy }}</div>

                <div>Filter.SourceTypeId: {{ item.Properties?.Filter?.SourceTypeId }}</div>
                <div>Filter.Type: {{ item.Properties?.Filter?.Type }}</div>

                <div>Filter.Search.terms: {{ item.Properties?.Filter?.Search?.terms }}</div>
                <div>Filter.Status: {{ item.Properties?.Filter?.Status }}</div>
                <div>Filter.UserId: {{ item.Properties?.Filter?.UserId }}</div>
                <div>Filter.InvoiceType: {{ item.Properties?.Filter?.InvoiceType }}</div>
                <div>Filter.Failure: {{ item.Properties?.Filter?.Failure }}</div>
                <div>Filter.Company: {{ item.Properties?.Filter?.Company }}</div>
                <div>Filter.Status: {{ item.Properties?.Filter?.Status }}</div>
                <div>Filter.Table: {{ item.Properties?.Filter?.Table }}</div>
                <div>Filter.Terminal: {{ item.Properties?.Filter?.Terminal }}</div>
                -->
              </template>

              <template #[`item.user`]="{item}">
                <user-chip
                  v-if="item.StaffId > 0"
                  :color="item.UserColor"
                  :full-name="item.User"
                  :short-name="item.UserShortName"
                />
              </template>

              <template #[`item.notify`]="{item}">
                <template v-if="item.Type?.endsWith('_download')">
                  <v-icon
                    color="teal"
                    size="27"
                  >
                    mdi-cloud-download
                  </v-icon>
                </template>

                <template v-else-if="item.Type?.endsWith('_print')">
                  <v-icon
                    color="brown lighten-1"
                    size="27"
                  >
                    mdi-printer
                  </v-icon>
                </template>

                <template v-else>
                  <reports-notify-details :report="item" />
                </template>
              </template>

              <template #[`item.download`]="{item}">
                <div v-if="item.DownloadUrl">
                  <v-badge
                    :color="item.Filetype === 'xlsx' ? 'green darken-1' : 'red lighten-1'"
                    icon="mdi-download"
                    light
                    offset-x="15"
                    offset-y="20"
                    overlap
                  >
                    <v-btn
                      :href="item.DownloadUrl"
                      icon
                      large
                      target="_blank"
                    >
                      <v-icon
                        :color="item.Filetype === 'xlsx' ? 'green darken-1' : 'red lighten-1'"
                        large
                      >
                        {{ item.Filetype === 'xlsx' ? 'mdi-microsoft-excel' : 'mdi-file-pdf-box' }}
                      </v-icon>
                    </v-btn>
                  </v-badge>
                </div>
              </template>

              <template #[`item.created_at`]="{item}">
                <div class="text-no-wrap">
                  {{ item.CreatedAt }}
                </div>
              </template>

              <template #[`item.actions`]="{item}">
                <template v-if="userCanDelete && userIsSuperAdmin">
                  <v-tooltip
                    :open-delay="1000"
                    color="red"
                    left
                  >
                    <template #activator="{on}">
                      <v-btn
                        :loading="item.loadingDelete"
                        :small="$vuetify.breakpoint.xsOnly"
                        class="elevation-2 ma-1 red"
                        dark
                        icon
                        @click="deleteReport(item)"
                        v-on="on"
                      >
                        <v-icon>{{ $t('Marketing.Sms.ListView.Button.Delete.Icon') }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Reports.ReportsNotify.ConfirmDeleteDialog.Title') }}</span>
                  </v-tooltip>
                </template>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <confirm-dialog
      :html-content="$t('Reports.ReportsNotify.ConfirmDeleteDialog.Content', {id: selectedItem?.Id})"
      :html-title="$t('Reports.ReportsNotify.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Reports.ReportsNotify.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteReport"
    />
  </div>
</template>

<script>
import Auth from '@/mixins/auth.js'
import AppData from '@/mixins/appdata.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import FilterCommon from '@/mixins/filter/filterCommon.js'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import {getContrastColor} from '@/lib/color/index.js'
import ReportsNotifyDetails from '@/components/reports/ReportsNotifyDetails.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import {clone} from '@/lib/helper/helper.js'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'ReportsNotify',
  components: {
    UserChip,
    ConfirmDialog,
    ReportsNotifyDetails,
    FilterOptions
  },
  directives: {},
  mixins    : [AppData, Auth, DataTable, FilterCommon],
  props     : {},
  data() {
    return {
      selectedItem              : null,
      detailsDialogVisible      : false,
      confirmDialogDeleteVisible: false,
      filtersModel              : {
        lang      : 'Reports.ReportsNotify',
        filterType: {
          key  : 'filter_type',
          value: null,
          items: this.$t('Reports.ReportsNotify.Filter.filterType.Items')
        },
        filterUser: {
          key  : 'filter_user',
          value: null,
          items: []
        },
        filterDate: {
          key    : 'filter_date',
          value  : null,
          visible: false,
          date   : null
        }
      },
      dataTable: {
        dataStore    : null,
        socketCommand: {
          list: window.SocketCommand.Reports.ReportsNotify.All
        },
        options: {
          defaultSortBy: 'created_at',
          sortBy       : ['created_at'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Id'),
            value    : 'id',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Type'),
            value    : 'type',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Range'),
            value    : 'range',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Properties'),
            value    : 'properties',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.User'),
            value    : 'user',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Notify'),
            value    : 'notify',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Download'),
            value    : 'download',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.CreatedAt'),
            value    : 'created_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Reports.ReportsNotify.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : '50px'
          }
        ]
      }
    }
  },
  computed: {},
  watch   : {},
  created() {
    this.$bus.$on(window.SocketCommand.Reports.ReportsNotify.Filters, this.onFiltersDataResponse)
    this.$bus.$on(window.SocketCommand.Reports.Delete, this.onDeleteReportResult)

    this.getFiltersData()

    // Init Custom Filter Values
    // this.filtersModel.filterAction.items = this.filterTypeItems

    if (this.$route?.params?.filterReportType) {
      setTimeout(() => {
        this.filtersModel.filterType.value = this.$route?.params?.filterReportType || null
      }, 250)
    }
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Reports.ReportsNotify.Filters, this.onFiltersDataResponse)
    this.$bus.$off(window.SocketCommand.Reports.Delete, this.onDeleteReportResult)
  },
  methods: {
    getContrastColor,

    getFiltersData() {
      window.callAS(window.SocketCommand.Reports.ReportsNotify.Filters, {})
    },
    onFiltersDataResponse(data) {
      this.filtersModel.filterUser.items = data?.Users || []
    },

    deleteReport(report) {
      if (!report) return
      this.selectedItem = report
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteReport() {
      const report = this.selectedItem
      window.callAS(window.SocketCommand.Reports.Delete, {Id: report.Id})
      this.selectedItem = null
    },
    onDeleteReportResult(result, payload) {
      if (result?.status === 'success') {
        this.getData()
      }
    },

    formatDate(dateStr) {
      // Extract the date portion ("2026-08-01") before the space
      const datePart = String(dateStr).split(' ')[0]

      // Split into year, month, and day
      const [year, month, day] = datePart.split('-')

      // Return in DD/MM/YYYY format
      return `${day}/${month}/${year}`
    },

    getPeriodName(period) {
      if (period === 'today') return this.$t('Common.Date.Today')
      if (period === 'week') return this.$t('Common.Date.Week')
      if (period === 'month') return this.$t('Common.Date.Month')
      if (period === 'months3') return this.$t('Common.Date.ThreeMonths')
      if (period === 'months6') return this.$t('Common.Date.SixMonths')
      if (period === 'year') return this.$t('Common.Date.Year')

      const [startDate, endDate] = period.split('_')
      const startDateFmt = this.formatDate(startDate)
      const endDateFmt = this.formatDate(endDate)

      return `${startDateFmt} - ${endDateFmt}`
    },

    getStaffName(id) {
      const staffList = clone(this.appConfig?.POS_USERS || [])
      return staffList.find(item => item.Id === id)?.Fullname || ''
    },

    getMachineName(guid) {
      const machines = clone(this.appMachines || [])
      const machine = machines.find(item => item.guid === guid)
      return machine ? machine?.alias ? `${machine?.hostname} ${machine?.alias}` : machine?.hostname : ''
    }
  }
}
</script>

<style scoped>

</style>
